import { useEffect, useState, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { NAV, scrollToId, REGISTER_URL } from "@/data/site";
import { useI18n } from "@/i18n/LanguageContext";
import { BrandLockup } from "@/components/site/Logos";
import { Button } from "@/components/ui/button";

const LangToggle = ({ scrolled }) => {
  const { lang, setLang } = useI18n();
  const opts = [
    { code: "en", label: "EN" },
    { code: "bn", label: "\u09ac\u09be\u0982" },
  ];
  const trackCls = scrolled ? "border-border bg-secondary" : "border-primary-foreground/25 bg-primary-foreground/10";
  const idleCls = scrolled ? "text-muted-foreground hover:text-primary" : "text-primary-foreground/75 hover:text-primary-foreground";
  return (
    <div className={`flex items-center gap-0.5 rounded-full border p-0.5 ${trackCls}`}>
      {opts.map((o) => (
        <button
          key={o.code}
          onClick={() => setLang(o.code)}
          aria-pressed={lang === o.code}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
            lang === o.code ? "bg-gold text-navy-deep shadow-sm" : idleCls
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNav = useCallback((id) => {
    setOpen(false);
    scrollToId(id);
  }, []);

  const linkIdle = scrolled ? "text-muted-foreground hover:text-primary" : "text-primary-foreground/80 hover:text-primary-foreground";
  const linkActive = scrolled ? "text-primary" : "text-primary-foreground";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "border-b border-border/70 bg-background/85 backdrop-blur-md shadow-[0_2px_20px_-12px_hsl(218_40%_20%_/_0.3)]"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="container-site section-pad flex h-[4.5rem] items-center justify-between gap-3">
        <button onClick={() => handleNav("home")} className="shrink-0" aria-label="Go to top">
          <BrandLockup />
        </button>

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV.map((link) => (
            <li key={link.id}>
              <button
                onClick={() => handleNav(link.id)}
                className={`relative rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  active === link.id ? linkActive : linkIdle
                }`}
              >
                {t.nav[link.key]}
                <span
                  className={`absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-gold transition-transform duration-300 ${
                    active === link.id ? "scale-x-100" : "scale-x-0"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2.5">
          <LangToggle scrolled={scrolled} />
          <div className="hidden lg:block">
            <Button asChild variant={scrolled ? "default" : "gold"} size="sm">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">{t.nav.register}</a>
            </Button>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors lg:hidden ${
              scrolled ? "text-primary hover:bg-muted" : "text-primary-foreground hover:bg-primary-foreground/10"
            }`}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden border-border bg-background/95 backdrop-blur-md transition-[max-height,opacity] duration-300 lg:hidden ${
          open ? "max-h-[28rem] border-t opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="container-site section-pad flex flex-col gap-1 py-4">
          {NAV.map((link) => (
            <li key={link.id}>
              <button
                onClick={() => handleNav(link.id)}
                className={`w-full rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                  active === link.id ? "bg-muted text-primary" : "text-muted-foreground hover:bg-muted/60"
                }`}
              >
                {t.nav[link.key]}
              </button>
            </li>
          ))}
          <li className="flex flex-col gap-2 pt-2">
            <Button asChild className="w-full" size="sm">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>{t.nav.register}</a>
            </Button>
            <Button variant="outline" className="w-full" size="sm" onClick={() => handleNav("schedule")}>
              {t.nav.viewSchedule}
            </Button>
          </li>
        </ul>
      </div>
    </header>
  );
};
